import {
    buildIrlCitationDetail,
    getCitationDisplayTitle,
    isIrlCitation,
    isObservationCitation,
} from './citationDisplay';
import { extractGroundingFields } from './intelligenceMeta';
import { computeManagementOmissionGaps } from './irlEvidenceOmissions';

export const VDR_RELEVANCE_LABELS = {
    STRONG: 'Strong evidence match',
    MODERATE: 'Moderate evidence match',
    CONTEXT: 'Related context',
    /** Retrieval returned this chunk; AI narrative did not use it to corroborate the claim. */
    NOT_CORROBORATED: 'Retrieved — not corroborated by AI',
};

export const IRL_RELATIONSHIP_LABELS = {
    SIMILAR: 'Similar claim',
    UNANSWERED: 'Related unanswered item',
    CROSS_REF: 'Cross-reference',
};

const SCORE_STRONG = 0.72;
const SCORE_MODERATE = 0.45;

function linkChunk(citation, chunks) {
    const idx = citation?.merged_chunk_index;
    if (Number.isInteger(idx) && chunks[idx]) return chunks[idx];
    return chunks.find(ch => ch.merged_chunk_index === idx) || null;
}

function citationScore(citation, linkedChunk) {
    const raw = citation?.evidence_score ?? linkedChunk?.['@search.score'] ?? linkedChunk?.retrieval_score;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

function isContradictingRole(role) {
    const r = String(role || '').toLowerCase();
    return r === 'contradicting_evidence' || r === 'contradicting';
}

export function deriveVdrRelevanceLabel(citation, linkedChunk, role) {
    const r = String(role || citation?.role || '').toLowerCase();
    const score = citationScore(citation, linkedChunk);

    if (isContradictingRole(r)) {
        return VDR_RELEVANCE_LABELS.CONTEXT;
    }

    if (r === 'supporting_evidence' || r === 'management_position') {
        if (score == null) return VDR_RELEVANCE_LABELS.STRONG;
        if (score >= SCORE_STRONG) return VDR_RELEVANCE_LABELS.STRONG;
        if (score >= SCORE_MODERATE) return VDR_RELEVANCE_LABELS.MODERATE;
        return VDR_RELEVANCE_LABELS.CONTEXT;
    }

    if (score == null) return VDR_RELEVANCE_LABELS.CONTEXT;
    if (score >= SCORE_STRONG) return VDR_RELEVANCE_LABELS.STRONG;
    if (score >= SCORE_MODERATE) return VDR_RELEVANCE_LABELS.MODERATE;
    return VDR_RELEVANCE_LABELS.CONTEXT;
}

export function deriveIrlRelationshipLabel(citation, linkedChunk) {
    const hasResponse = citation?.has_response ?? linkedChunk?.has_response;
    if (hasResponse === false) return IRL_RELATIONSHIP_LABELS.UNANSWERED;

    const intent = String(citation?.intent_class || linkedChunk?.chunk_intent || '').toLowerCase();
    if (
        intent.includes('similar') ||
        intent.includes('duplicate') ||
        intent.includes('parallel') ||
        intent.includes('claim')
    ) {
        return IRL_RELATIONSHIP_LABELS.SIMILAR;
    }

    if (hasResponse === true) return IRL_RELATIONSHIP_LABELS.SIMILAR;
    return IRL_RELATIONSHIP_LABELS.CROSS_REF;
}

export function deriveVdrWhyThisMatters(citation, linkedChunk, relevanceLabel, role) {
    const r = String(role || citation?.role || '').toLowerCase();
    if (isContradictingRole(r)) {
        return 'Retrieved material may conflict with the management position.';
    }
    if (relevanceLabel === VDR_RELEVANCE_LABELS.NOT_CORROBORATED) {
        return 'Retrieved by search similarity; the AI validation above did not treat this as proof of the management claim.';
    }
    if (relevanceLabel === VDR_RELEVANCE_LABELS.STRONG) {
        return 'Directly addresses the diligence question or substantiates the management response.';
    }
    if (relevanceLabel === VDR_RELEVANCE_LABELS.MODERATE) {
        return 'Provides partial context or supporting detail for validation.';
    }
    return 'Background context from the data room; use alongside stronger documentary matches.';
}

/** When AI narrative rejects corroboration, do not show score-based “Strong evidence match” on cards. */
export function capVdrRelevanceForValidation(label, downgrade) {
    if (!downgrade) return label;
    if (
        label === VDR_RELEVANCE_LABELS.STRONG ||
        label === VDR_RELEVANCE_LABELS.MODERATE
    ) {
        return VDR_RELEVANCE_LABELS.NOT_CORROBORATED;
    }
    return label;
}

function shouldDowngradeVdrLabels(options = {}) {
    if (options.downgradeVdrLabels === true) return true;
    if (options.downgradeVdrLabels === false) return false;
    const verdict = String(options.supportVerdict || '').toLowerCase();
    if (
        verdict === 'no_vdr_evidence' ||
        verdict === 'not_supported' ||
        verdict === 'contradicted'
    ) {
        return true;
    }
    const narrative = options.narrative;
    if (narrative?.overridesSummary && (narrative.severity === 'none' || narrative.severity === 'weak')) {
        return true;
    }
    const level = String(options.supportLevel || '').toLowerCase();
    return (
        level === 'not_corroborated' ||
        level === 'no_vdr_evidence' ||
        level === 'not_supported' ||
        level === 'does_not_match' ||
        level === 'insufficient_evidence'
    );
}

/**
 * When the API returns fused evidence rows but governance stripped citation refs,
 * synthesize citation rows from chunks so the panel can render documentary vs IRL groups.
 */
function citationsFromChunks(chunks = []) {
    return (Array.isArray(chunks) ? chunks : []).map((ch, index) => ({
        ref: `[${index + 1}]`,
        merged_chunk_index: Number.isInteger(ch.merged_chunk_index) ? ch.merged_chunk_index : index,
        source_type: ch.source_type,
        file_id: ch.file_id,
        document_name: ch.document_name || ch.metadata_storage_name || ch.file_name,
        metadata_storage_name: ch.metadata_storage_name,
        file_name: ch.file_name,
        text_preview: ch.chunk,
        irl_ref: ch.irl_ref,
        irl_id: ch.irl_id,
        tag: ch.tag,
        workstream_name: ch.workstream_name,
        workstream_id: ch.workstream_id,
        request_excerpt: ch.request_excerpt,
        answer_excerpt: ch.answer_excerpt,
        response_status: ch.response_status,
        has_response: ch.has_response,
        intent_class: ch.intent_class,
        role: ch.role,
        evidence_score: ch['@search.score'],
    }));
}

/**
 * Split citations into VDR documentary (primary), VDR contradicting (gaps), and related IRL (secondary).
 */
export function groupEvidenceCitations(citations = [], chunks = [], options = {}) {
    let effectiveCitations = Array.isArray(citations) ? citations : [];
    const chunkList = Array.isArray(chunks) ? chunks : [];
    if (effectiveCitations.length === 0 && chunkList.length > 0) {
        effectiveCitations = citationsFromChunks(chunkList);
    }

    const sourceIrlId = options.sourceIrlId != null ? Number(options.sourceIrlId) : null;

    const documentary = [];
    const contradictingVdr = [];
    const relatedIrl = [];
    const savedObservations = [];

    for (const citation of effectiveCitations) {
        const linkedChunk = linkChunk(citation, chunks);
        const entry = { citation, linkedChunk };
        const role = String(citation?.role || '').toLowerCase();

        if (isObservationCitation(citation, linkedChunk)) {
            savedObservations.push({
                ...entry,
                displayTitle: getCitationDisplayTitle(citation, linkedChunk, {}),
            });
            continue;
        }

        if (isIrlCitation(citation, linkedChunk)) {
            const citeIrlId = Number(citation?.irl_id ?? linkedChunk?.irl_id);
            const isSourceIrl =
                sourceIrlId != null && Number.isFinite(citeIrlId) && citeIrlId === sourceIrlId;
            if (isSourceIrl) {
                continue;
            }
            relatedIrl.push({
                ...entry,
                relationshipLabel: deriveIrlRelationshipLabel(citation, linkedChunk),
                detail: buildIrlCitationDetail(citation, linkedChunk),
            });
            continue;
        }

        const rawRelevance = deriveVdrRelevanceLabel(citation, linkedChunk, role);
        const downgrade = shouldDowngradeVdrLabels(options);
        const relevanceLabel = capVdrRelevanceForValidation(rawRelevance, downgrade);
        const enriched = {
            ...entry,
            relevanceLabel,
            rawRelevanceLabel: downgrade ? rawRelevance : undefined,
            whyThisMatters: deriveVdrWhyThisMatters(citation, linkedChunk, relevanceLabel, role),
            displayTitle: getCitationDisplayTitle(citation, linkedChunk, {}),
            debugScore: citationScore(citation, linkedChunk),
        };

        if (isContradictingRole(role)) {
            contradictingVdr.push(enriched);
        } else {
            documentary.push(enriched);
        }
    }

    const sortByRelevance = (a, b) => {
        const order = {
            [VDR_RELEVANCE_LABELS.STRONG]: 0,
            [VDR_RELEVANCE_LABELS.MODERATE]: 1,
            [VDR_RELEVANCE_LABELS.NOT_CORROBORATED]: 2,
            [VDR_RELEVANCE_LABELS.CONTEXT]: 3,
        };
        const da = order[a.relevanceLabel] ?? 3;
        const db = order[b.relevanceLabel] ?? 3;
        if (da !== db) return da - db;
        const sa = a.debugScore ?? -1;
        const sb = b.debugScore ?? -1;
        return sb - sa;
    };

    documentary.sort(sortByRelevance);

    return { documentary, contradictingVdr, relatedIrl, savedObservations };
}

export function buildEvidenceMixSummary(grouped, observationCandidates = []) {
    const obs = Array.isArray(observationCandidates) ? observationCandidates : [];
    return {
        documentsFound: (grouped?.documentary?.length || 0) + (grouped?.contradictingVdr?.length || 0),
        relatedIrlsFound: grouped?.relatedIrl?.length || 0,
        observationCandidatesCount: obs.length,
    };
}

function gapText(g) {
    if (!g) return '';
    if (typeof g === 'string') return g.trim();
    return String(g.description || g.summary || g.message || g.title || '').trim();
}

function isContradictionObservation(obs) {
    const ft = String(obs?.finding_type || '').toLowerCase();
    return ft.includes('contradict') || ft.includes('misalign');
}

export function collectGapItems({
    alignment,
    result,
    grouped,
    assessment,
    diligenceQuestion = '',
    managementAnswer = '',
}) {
    const items = [];
    const seen = new Set();

    const push = (text, severity = 'amber', kind = 'gap') => {
        const t = String(text || '').trim();
        if (!t || seen.has(t)) return;
        seen.add(t);
        items.push({ text: t, severity, kind });
    };

    for (const g of alignment?.gaps || []) {
        const type = g?.type ? `[${g.type}] ` : '';
        push(`${type}${gapText(g)}`, 'amber', 'alignment_gap');
    }

    for (const w of assessment?.grounding?.warnings || []) {
        push(typeof w === 'string' ? w : w.message || w.code, 'amber', 'governance');
    }

    const unsupported = Number(assessment?.grounding?.unsupported_claim_count) || 0;
    if (unsupported > 0) {
        push(
            `${unsupported} unsupported claim${unsupported === 1 ? '' : 's'} in the generated validation narrative`,
            'red',
            'unsupported',
        );
    }

    if (assessment?.grounding?.hallucination_risk_flag) {
        push('Elevated narrative integrity risk — verify citations before relying on the answer', 'red', 'integrity');
    }

    for (const { citation, linkedChunk, displayTitle, relevanceLabel } of grouped?.contradictingVdr || []) {
        const title = displayTitle || getCitationDisplayTitle(citation, linkedChunk, {});
        push(`Contradicting VDR evidence: ${title}`, 'red', 'vdr_contradiction');
        if (linkedChunk?.chunk || citation?.text_preview) {
            const excerpt = (linkedChunk?.chunk || citation?.text_preview || '').slice(0, 160);
            if (excerpt) push(excerpt, 'red', 'vdr_contradiction_excerpt');
        }
        void relevanceLabel;
    }

    for (const obs of result?.observation_candidates || []) {
        if (!isContradictionObservation(obs)) continue;
        push(obs.title || obs.summary || obs.description, 'red', 'observation');
        if (obs.description && obs.title) push(obs.description, 'red', 'observation_detail');
    }

    const weakSupport = String(alignment?.weak_support_summary || alignment?.weak_evidence_summary || '').trim();
    if (weakSupport) push(weakSupport, 'amber', 'weak_support');

    const missing = String(alignment?.missing_evidence_summary || '').trim();
    if (missing) push(missing, 'amber', 'missing_evidence');

    const omissionGaps = computeManagementOmissionGaps({
        diligenceQuestion,
        managementAnswer,
        grouped,
        rawAnswer: result?.final_answer || result?.answer,
        parsedAnswer: assessment?.parsedAnswer,
    });
    for (const gap of omissionGaps) {
        push(gap.text, 'amber', 'management_omission');
    }

    return items;
}

export function collectFollowUpQuestions({ alignment, observationCandidates = [], gapItems = [] }) {
    const seen = new Set();
    const out = [];

    const add = q => {
        const t = String(q || '').trim();
        if (!t || seen.has(t)) return;
        seen.add(t);
        out.push(t);
    };

    for (const q of alignment?.recommended_follow_up_questions || []) add(q);

    for (const obs of observationCandidates) {
        for (const q of obs?.follow_up_questions || []) add(q);
    }

    for (const gap of gapItems) {
        if (gap.kind !== 'alignment_gap') continue;
        const t = gap.text;
        if (t.length > 12 && t.length < 280 && /\?/.test(t)) add(t);
    }

    return out;
}

export function buildEvidenceDebugStats(result, grouped) {
    const retrieval = result?.retrieval_debug || {};
    const counts = retrieval.candidate_counts || retrieval;
    const grounding = extractGroundingFields(result?.evidence_governance, retrieval);
    const citations = result?.citations || [];

    const stats = [];
    if (counts.files_searched != null) stats.push({ label: 'Files searched', value: counts.files_searched });
    if (counts.irls_analyzed != null) stats.push({ label: 'IRLs analyzed', value: counts.irls_analyzed });
    if (counts.chunks_used != null) stats.push({ label: 'Chunks used', value: counts.chunks_used });
    stats.push({ label: 'VDR citations', value: grouped?.documentary?.length + grouped?.contradictingVdr?.length || 0 });
    stats.push({ label: 'IRL citations', value: grouped?.relatedIrl?.length || 0 });
    stats.push({ label: 'Total citations', value: citations.length });
    if (grounding.citation_coverage != null) {
        const n = Number(grounding.citation_coverage);
        const pct = Number.isFinite(n) ? (n <= 1 ? Math.round(n * 100) : Math.round(n)) : grounding.citation_coverage;
        stats.push({ label: 'Citation coverage', value: `${pct}%` });
    }
    if (grounding.grounding_status) stats.push({ label: 'Grounding', value: String(grounding.grounding_status).replace(/_/g, ' ') });
    if (result?.trace_id) stats.push({ label: 'Trace ID', value: result.trace_id });
    if (retrieval.filename_match_detected != null) {
        stats.push({ label: 'Filename match', value: retrieval.filename_match_detected ? 'Yes' : 'No' });
    }
    if (retrieval.filename_resolution_status) {
        stats.push({ label: 'Filename resolution', value: retrieval.filename_resolution_status });
    }
    if (retrieval.semantic_fallback || retrieval.filename_fallback_semantic) {
        stats.push({ label: 'Semantic retrieval', value: 'Used' });
    }
    const routerIntent = retrieval.router_intent || retrieval.intent;
    if (routerIntent) {
        stats.push({ label: 'Router intent', value: String(routerIntent).replace(/_/g, ' ') });
    }
    const vdrHits = retrieval.vdr_hit_count;
    const irlHits = retrieval.irl_hit_count;
    if (vdrHits != null) stats.push({ label: 'VDR hits retrieved', value: vdrHits });
    if (irlHits != null) stats.push({ label: 'IRL hits retrieved', value: irlHits });
    if (retrieval.permitted_vdr_file_count != null) {
        stats.push({ label: 'Permitted VDR files in scope', value: retrieval.permitted_vdr_file_count });
    }
    if (retrieval.fused_count != null) stats.push({ label: 'Fused evidence count', value: retrieval.fused_count });
    const mix = retrieval.final_evidence_mix;
    if (mix && typeof mix === 'object') {
        if (mix.vdr_chunk != null) stats.push({ label: 'Fused VDR chunks', value: mix.vdr_chunk });
        if (mix.irl != null) stats.push({ label: 'Fused IRL items', value: mix.irl });
    }
    const fb = retrieval.fallbacks;
    if (fb && typeof fb === 'object') {
        if (fb.vdr_error) stats.push({ label: 'VDR retrieval error', value: String(fb.vdr_error) });
        if (fb.irl_error) stats.push({ label: 'IRL retrieval error', value: String(fb.irl_error) });
    }

    return stats;
}

/** Future evidence source slots (not wired). */
export const FUTURE_EVIDENCE_GROUPS = [
    { id: 'observations', label: 'Observations' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'raid', label: 'RAID' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'maturity', label: 'Maturity' },
    { id: 'security', label: 'Security governance' },
];
